import { useState, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin,
  closestCenter,
  type CollisionDetection,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCard, KanbanCardContent } from './KanbanCard';
import { computeDropStates, type DropState, type MinimalWorkflowPhase } from '../lib/move-rules';
import { getUnresolvedBlockers } from '@shipdit/core';
import { cn, getPhaseColor } from '../lib/utils';
import type { Board, Card, Column } from '../lib/api';

interface Props {
  board: Board;
  cards: Card[];
  workflow?: MinimalWorkflowPhase[];
  onMoveCard: (cardId: string, columnId: string, order?: number) => void;
  onCardClick: (card: Card) => void;
  onBlockedDrop?: () => void;
  /** Called when the user clicks the empty-state area of a creatable column. */
  onCreatableColumnEmptyClick?: (columnId: string) => void;
  /** Set of column IDs where users may create cards directly. */
  creatableColumnIds?: Set<string>;
}

/**
 * Composite collision detection: try pointerWithin first (works well for
 * cards and filled columns), fall back to closestCenter (catches empty columns
 * that the pointer can't be "within").
 *
 * On touch devices, the DragOverlay element can occlude droppable targets and
 * cause pointerWithin to return only 1 result (the overlay itself). We only
 * trust pointerWithin when it finds 2+ candidates (real column content, not
 * just overlay artifacts), otherwise use closestCenter.
 */
const compositeCollision: CollisionDetection = (args) => {
  const pw = pointerWithin(args);
  // Only trust pointerWithin if it found multiple candidates (not just the overlay)
  if (pw.length > 1) return pw;
  const cc = closestCenter(args);
  if (cc.length > 0) return cc;
  return pw; // last resort
};

const DONE_PAGE_SIZE = 5;

export function KanbanBoard({ board, cards, workflow, onMoveCard, onCardClick, onBlockedDrop, onCreatableColumnEmptyClick, creatableColumnIds }: Props) {

  const [activeCard, setActiveCard] = useState<Card | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const [doneVisibleCount, setDoneVisibleCount] = useState(DONE_PAGE_SIZE);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Build a map from column status → workflow phase color
  const phaseColorMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!workflow) return map;
    for (const phase of workflow) {
      if (phase.color) {
        map.set(phase.name, phase.color);
      }
    }
    return map;
  }, [workflow]);

  // Determine which phases are auto-advance (driven by template workflow)
  const autoAdvanceStatuses = useMemo(() => {
    if (!workflow) return new Set<string>();
    return new Set(
      workflow
        .filter((p) => p.autoAdvance)
        .map((p) => p.name)
    );
  }, [workflow]);

  const isAgentPhase = useCallback(
    (status: string) => autoAdvanceStatuses.has(status),
    [autoAdvanceStatuses]
  );

  // Determine which phases are disabled
  const disabledStatuses = useMemo(() => {
    if (!workflow) return new Set<string>();
    return new Set(
      workflow
        .filter((p) => p.disabled)
        .map((p) => p.name)
    );
  }, [workflow]);

  const isDisabledPhase = useCallback(
    (status: string) => disabledStatuses.has(status),
    [disabledStatuses]
  );

  // Precompute drop states for all columns when a card is being dragged
  const dropStates = useMemo<Map<string, DropState>>(() => {
    if (!activeCard) return new Map();
    return computeDropStates(board.columns, activeCard.columnId, workflow);
  }, [activeCard, board.columns, workflow]);

  // Precompute blocker state per card: cardId → unresolved blocker count.
  // "Blocked" means: the card has blockedBy IDs whose target cards are not
  // yet in a done-status column. Used for a visual badge + drag-time guard.
  const blockerCountByCardId = useMemo(() => {
    const map = new Map<string, number>();
    for (const card of cards) {
      if (!card.metadata.blockedBy || card.metadata.blockedBy.length === 0) continue;
      const unresolved = getUnresolvedBlockers(card, cards, board.columns);
      if (unresolved.length > 0) map.set(card.id, unresolved.length);
    }
    return map;
  }, [cards, board.columns]);

  const cardsByColumn = useMemo(() => {
    const map = new Map<string, Card[]>();
    board.columns.forEach((col) => map.set(col.id, []));
    cards.forEach((card) => {
      const list = map.get(card.columnId);
      if (list) list.push(card);
    });
    map.forEach((list) => list.sort((a, b) => a.order - b.order));
    return map;
  }, [board.columns, cards]);

  function handleDragStart(event: DragStartEvent) {
    const card = cards.find((c) => c.id === event.active.id);
    if (card) setActiveCard(card);
  }

  function handleDragOver(event: DragOverEvent) {
    const overId = event.over?.id as string | undefined;
    if (!overId) { setOverColumnId(null); return; }
    const isColumn = board.columns.some((c) => c.id === overId);
    if (isColumn) { setOverColumnId(overId); return; }
    const overCard = cards.find((c) => c.id === overId);
    if (overCard) setOverColumnId(overCard.columnId);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveCard(null);
    setOverColumnId(null);
    if (!over) return;

    const cardId = active.id as string;
    const overId = over.id as string;

    // Look up the dragged card from stable query data (not React state)
    const draggedCard = cards.find((c) => c.id === cardId);
    if (!draggedCard) return;

    // Determine target column
    const isColumn = board.columns.some((c) => c.id === overId);
    const targetColumnId = isColumn ? overId : cards.find((c) => c.id === overId)?.columnId;
    if (!targetColumnId) return;

    // Compute drop validity directly — don't rely on the useMemo value which
    // can become stale if @dnd-kit triggers a synchronous re-render after
    // setActiveCard(null) above.
    const freshStates = computeDropStates(board.columns, draggedCard.columnId, workflow);
    const state = freshStates.get(targetColumnId);
    if (state === 'blocked') {
      onBlockedDrop?.();
      return;
    }

    // Prerequisite guard: block forward moves for cards with unresolved
    // prerequisite dependencies. Reorder within the same column is fine.
    if (
      (blockerCountByCardId.get(draggedCard.id) ?? 0) > 0 &&
      draggedCard.columnId !== targetColumnId
    ) {
      const sortedForBlockCheck = [...board.columns].sort((a, b) => a.order - b.order);
      const fromIdx = sortedForBlockCheck.findIndex((c) => c.id === draggedCard.columnId);
      const toIdx = sortedForBlockCheck.findIndex((c) => c.id === targetColumnId);
      if (toIdx > fromIdx) {
        onBlockedDrop?.();
        return;
      }
    }

    // Calculate order
    const columnCards = cardsByColumn.get(targetColumnId) || [];
    const overCardIndex = columnCards.findIndex((c) => c.id === overId);
    const order = overCardIndex >= 0 ? overCardIndex : columnCards.length;

    onMoveCard(cardId, targetColumnId, order);
  }

  function handleDragCancel() {
    setActiveCard(null);
    setOverColumnId(null);
  }

  const sortedColumns = useMemo(
    () => [...board.columns].sort((a, b) => a.order - b.order),
    [board.columns]
  );

  return (
      <DndContext
        sensors={sensors}
        collisionDetection={compositeCollision}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
          {/* Desktop: phase lanes (rows) with horizontal card scrolling — shown at lg+ */}
          <div className="hidden h-full flex-col gap-2 overflow-y-auto p-6 lg:flex">
            {sortedColumns.map((column) => {
                const columnCards = cardsByColumn.get(column.id) || [];
                const isDoneColumn = column.status === 'done';
                const visibleCards = isDoneColumn
                  ? [...columnCards].sort((a, b) => b.order - a.order).slice(0, doneVisibleCount)
                  : columnCards;
                const hiddenCount = isDoneColumn ? Math.max(0, columnCards.length - doneVisibleCount) : 0;
                return (
                  <KanbanColumn
                    key={column.id}
                    column={column}
                    cardCount={columnCards.length}
                    isOver={overColumnId === column.id}
                    dropState={dropStates.get(column.id)}
                    isDragging={!!activeCard}
                    isAgentPhase={isAgentPhase(column.status)}
                    isDisabled={isDisabledPhase(column.status)}
                    phaseColor={phaseColorMap.get(column.status)}
                    onEmptyClick={creatableColumnIds?.has(column.id) ? () => onCreatableColumnEmptyClick?.(column.id) : undefined}
                  >
                    <SortableContext items={visibleCards.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
                      {visibleCards.map((card) => {
                        const blockerCount = blockerCountByCardId.get(card.id) ?? 0;
                        return (
                          <KanbanCard
                            key={card.id}
                            card={card}
                            onClick={() => onCardClick(card)}
                            className="w-72 shrink-0"
                            isBlocked={blockerCount > 0}
                            blockerCount={blockerCount}
                          />
                        );
                      })}
                    </SortableContext>
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => setDoneVisibleCount((v) => v + DONE_PAGE_SIZE)}
                        className="flex shrink-0 items-center gap-1.5 self-center rounded-lg px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        Load {Math.min(DONE_PAGE_SIZE, hiddenCount)} more
                      </button>
                    )}
                  </KanbanColumn>
                );
              })}
          </div>

          {/* Mobile: vertical phases with horizontal card swiping — shown below lg */}
          <div className="flex flex-col gap-1 overflow-y-auto px-3 py-4 lg:hidden">
            {sortedColumns.map((column, colIdx) => {
              const columnCards = cardsByColumn.get(column.id) || [];
              const isDoneColumn = column.status === 'done';
              const mobileVisibleCards = isDoneColumn
                ? [...columnCards].sort((a, b) => b.order - a.order).slice(0, doneVisibleCount)
                : columnCards;
              const mobileHiddenCount = isDoneColumn ? Math.max(0, columnCards.length - doneVisibleCount) : 0;
              const nextColumn = sortedColumns[colIdx + 1] ?? null;
              return (
                <MobilePhaseRow
                  key={column.id}
                  column={column}
                  cards={mobileVisibleCards}
                  totalCardCount={columnCards.length}
                  hiddenCount={mobileHiddenCount}
                  onLoadMore={mobileHiddenCount > 0 ? () => setDoneVisibleCount((v) => v + DONE_PAGE_SIZE) : undefined}
                  isAgentPhase={isAgentPhase(column.status)}
                  isDisabled={isDisabledPhase(column.status)}
                  phaseColor={phaseColorMap.get(column.status)}
                  nextColumn={nextColumn}
                  workflow={workflow}
                  onCardClick={onCardClick}
                  onMoveCard={onMoveCard}
                  onEmptyClick={creatableColumnIds?.has(column.id) ? () => onCreatableColumnEmptyClick?.(column.id) : undefined}
                  blockerCountByCardId={blockerCountByCardId}
                  onBlockedMove={onBlockedDrop}
                />
              );
            })}
          </div>

        <DragOverlay dropAnimation={null} modifiers={[restrictToWindowEdges]}>
          {activeCard ? (
            <div className="w-72 opacity-80">
              <KanbanCardContent card={activeCard} isDragOverlay />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
  );
}

/** Mobile: collapsible phase row with horizontally-scrollable cards */
function MobilePhaseRow({
  column,
  cards,
  totalCardCount,
  hiddenCount,
  onLoadMore,
  isAgentPhase,
  isDisabled,
  phaseColor,
  nextColumn,
  workflow,
  onCardClick,
  onMoveCard,
  onEmptyClick,
  blockerCountByCardId,
  onBlockedMove,
}: {
  column: Column;
  cards: Card[];
  totalCardCount?: number;
  hiddenCount?: number;
  onLoadMore?: () => void;
  isAgentPhase: boolean;
  isDisabled: boolean;
  phaseColor?: string;
  nextColumn: Column | null;
  workflow?: MinimalWorkflowPhase[];
  onCardClick: (card: Card) => void;
  onMoveCard: (cardId: string, columnId: string, order?: number) => void;
  onEmptyClick?: () => void;
  blockerCountByCardId?: Map<string, number>;
  onBlockedMove?: () => void;
}) {
  const [userOverride, setUserOverride] = useState<boolean | null>(null);
  // Auto-expand when cards arrive; respect explicit user collapse/expand if set
  const expanded = userOverride !== null ? userOverride : cards.length > 0;

  // A card can move forward only if the current phase is not auto-advance and
  // there is a next column to move to.
  const canMoveToNext = !isAgentPhase && nextColumn !== null;

  return (
    <div className={cn('rounded-lg', isDisabled && 'opacity-60')}>
      {/* Phase header — tap to expand/collapse */}
      <button
        onClick={() => setUserOverride(!expanded)}
        className="flex w-full items-center gap-2 px-2 py-2.5"
      >
        <svg className={cn('h-3 w-3 text-gray-500 transition-transform', expanded && 'rotate-90')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <span className={cn('h-2 w-2 rounded-full', getPhaseColor(phaseColor).dot)} />
        <span className={cn('text-xs font-semibold uppercase tracking-wider text-gray-400', isDisabled && 'line-through')}>
          {column.name}
        </span>
        {isAgentPhase && (
          <span className="rounded-md bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-medium text-purple-400">Auto</span>
        )}
        <span className="ml-auto rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-400">
          {totalCardCount ?? cards.length}
        </span>
      </button>

      {/* Cards — horizontal scroll */}
      {expanded && (
        <div className="pb-2">
          {cards.length > 0 ? (
            <div className="mobile-phase-scroll flex gap-2.5 overflow-x-auto px-2 pb-1">
              {cards.map((card) => {
                const isLocked = !!(card.metadata.claim && new Date(card.metadata.claim.expiresAt) > new Date());
                const dropStates = canMoveToNext && nextColumn
                  ? computeDropStates([column, nextColumn], column.id, workflow)
                  : null;
                const blockerCount = blockerCountByCardId?.get(card.id) ?? 0;
                const isBlocked = blockerCount > 0;
                const nextMoveAllowed = canMoveToNext && nextColumn && !isLocked
                  && !isBlocked
                  && dropStates?.get(nextColumn.id) === 'valid';
                return (
                  <div key={card.id} className="w-64 shrink-0">
                    <div className="relative">
                      <KanbanCard
                        card={card}
                        onClick={() => onCardClick(card)}
                        disableDrag
                        isBlocked={isBlocked}
                        blockerCount={blockerCount}
                      />
                      {isBlocked && canMoveToNext && nextColumn && !isLocked && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onBlockedMove?.();
                          }}
                          className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-gray-800/90 px-2 py-1 text-[10px] font-medium text-gray-400 shadow cursor-not-allowed"
                          title={`Blocked by ${blockerCount} unresolved prerequisite${blockerCount === 1 ? '' : 's'}`}
                        >
                          🔒 Blocked
                        </button>
                      )}
                      {nextMoveAllowed && nextColumn && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveCard(card.id, nextColumn.id);
                          }}
                          className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-gray-700/90 px-2 py-1 text-[10px] font-medium text-gray-300 shadow hover:bg-gray-600 active:bg-gray-500"
                          title={`Move to ${nextColumn.name}`}
                        >
                          <span className="max-w-[80px] truncate">{nextColumn.name}</span>
                          <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              onClick={onEmptyClick}
              className={cn(
                'mx-2 flex h-16 items-center justify-center rounded-lg border border-dashed border-gray-800 text-xs text-gray-600',
                onEmptyClick && 'cursor-pointer active:bg-gray-800/40'
              )}
            >
              {onEmptyClick ? '+ Add a card' : 'No cards'}
            </div>
          )}
          {onLoadMore && hiddenCount && hiddenCount > 0 && (
            <button
              onClick={onLoadMore}
              className="mt-1 flex w-full items-center justify-center gap-1.5 py-1 text-xs text-gray-500 active:text-gray-300"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              Load {Math.min(DONE_PAGE_SIZE, hiddenCount)} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
